import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import TextInput from "../../../components/TextInput";
import Dropdown from "components/Dropdown";
import CustomTextarea from "components/CustomTextarea";
import CheckboxButton from "../../../components/CheckboxButton";
import CustomButton from "../../../components/CustomButton";
import { getItemsDay, getItemsMonth, getItemsYear, getStableFile, downloadFileAttachment } from "../../../utils/util";
import BaseFormSection from "./BaseFormSection";
import PolicySection from "./PolicySection";
import { LoadingIcon, LoadingSpinner } from "components/Icons";
import FileUploader from "../../../components/FileUploader";
import { formatDate } from "../../../utils/format";
import CustomIconButton from "../../../components/CustomIconButton";
import CustomTag from "../../../components/CustomTag";
import { TALENT_TYPE } from "../../../constants/index";
import FileItem from "../../../components/FileItem";
import { uploadFileRegistration } from "../../../reducers/talentRegister";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import CustomToast from "../../../components/CustomToast";
import i18n from 'i18next';

const RegistrationForm = ({
    formData, setFormData, errors, onSubmit, onChange, onBlur,
    options, setters, getValueByObject
}) => {
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    const { companyInfo, isLoadingRegister, talentOptionsRelatedRegister, type, reasonListItems
    } = useSelector((state: any) => state.talentRegister);
    const [searchParams] = useSearchParams();
    const token = searchParams.get("t");

    const jobType1Options = options.jobType1Options;
    const jobType2Options = options.jobType2Options;
    const jobType3Options = options.jobType3Options;
    const industry1Options = options.industry1Options;
    const industry2Options = options.industry2Options;
    const industry3Options = options.industry3Options;
    
    const dataMonth = getItemsMonth();
    const dataYear = getItemsYear(null);
    const [isAgree, setIsAgree] = useState(false);
    const [reasonList, setReasonList] = useState(reasonListItems);
    const [fileLoading, setFileLoading] = useState<Record<string, boolean>>({});

    const showToast = (title: string, message: string, variant: string) => {
        enqueueSnackbar("I love snacks.", {
        autoHideDuration: 7000,
        content: (key) => (
            <CustomToast title={title} message={message} variant={variant} snackbarKey={key} closeSnackbar={closeSnackbar} />
        ),
        });
    };
    
    const handleChangeAgree = () => {
        setIsAgree(prev => !prev)
    };

    const handleChangeJobTypeLevel1 = (value) => {
        setFormData((prev: any) => ({
            ...prev,
            profession_1: value,
            profession_2: null,
            profession_3: null,
        }));

        // Reload options level 2
        if (value && value.code) {
            setters.setJobType2Options(talentOptionsRelatedRegister?.jobTypeOptions.filter(item => item.parent_id == value.code && item.level == 2))
        } else {
            setters.setJobType2Options([])
        }

        // Reload options level 3
        setters.setJobType3Options([])
    }

    const handleChangeJobTypeLevel2 = (value) => {
        setFormData((prev: any) => ({
            ...prev,
            profession_2: value,
            profession_3: null,
        }));

        // Reload options level 3
        if (value && value.code) {
            setters.setJobType3Options(talentOptionsRelatedRegister?.jobTypeOptions.filter(item => item.parent_id == value.code && item.level == 3))
        } else {
            setters.setJobType3Options([])
        }
    }

    const handleChangeIndustryLevel1 = (value) => {
        setFormData((prev: any) => ({
            ...prev,
            industry1: value,
            industry2: null,
            industry3: null,
        }));

        // Reload options level 2
        if (value && value.code) {
            setters.setIndustry2Options(talentOptionsRelatedRegister?.industryOptions.filter(item => item.parent_id == value.code && item.level == 2))
        } else {
            setters.setIndustry2Options([])
        }

        // Reload options level 3
        setters.setIndustry3Options([])
    }

    const handleChangeIndustryLevel2 = (value) => {
        setFormData((prev: any) => ({
            ...prev,
            industry2: value,
            industry3: null,
        }));

        // Reload options level 3
        if (value && value.code) {
            setters.setIndustry3Options(talentOptionsRelatedRegister?.industryOptions.filter(item => item.parent_id == value.code && item.level == 3))
        } else {
            setters.setIndustry3Options([])
        }
    }

    const changeYearOfLeave = (value: any) => {
        let dateOfLeave = formData.dateOfLeave
    
        if(value && getValueByObject(value) && formData.monthOfLeave && getValueByObject(formData.monthOfLeave)) {
            const dataDate = getItemsDay(
                getValueByObject(value),
                getValueByObject(formData.monthOfLeave),
            );
            setters.setDayOfLeaveOptions(dataDate);
        
            if(dateOfLeave && !dataDate.some(item => item.id === dateOfLeave.id)) {
                dateOfLeave = null
            }
        }
    
        setFormData((prev) => ({ 
            ...prev, 
            yearOfLeave: value, 
            dateOfLeave: dateOfLeave 
        }))
    };

    const changeMonthOfLeave = (value: any) => {
        let dateOfLeave = formData.dateOfLeave

        if(formData.yearOfLeave && getValueByObject(formData.yearOfLeave) && value && getValueByObject(value)) {
            const dataDate = getItemsDay(
                getValueByObject(formData.yearOfLeave),
                getValueByObject(value),
            );
            setters.setDayOfLeaveOptions(dataDate);

            if(dateOfLeave && !dataDate.some(item => item.id === dateOfLeave.id)) {
                dateOfLeave = null
            }
        }

        setFormData((prev) => ({ 
            ...prev, 
            monthOfLeave: value, 
            dateOfLeave: dateOfLeave 
        }))
    };

    const handleCheckboxChange = (item: any, index: any) => {
        setReasonList((prev:any) => prev.map((con:any, idx:any) => idx === index ? ({...con, checked: !con.checked}) : con));

        if (!item?.checked) {
            setters.setSelectedOptions((prev) => [...prev, item?.value]);

            if(item?.value == 'その他') {
                setters.setShowOtherReason(true)
            }
        } else {
            setters.setSelectedOptions((prev) => prev.filter((option) => option !== item?.value));
            if(item?.value == 'その他') {
                setters.setShowOtherReason(false)
            }
        }
    };

    const changeOtherReason = (event: any) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleChangeFile = async (file:any, item) => {
        if (Object.values(fileLoading).some((value) => value)) {
            showToast('メッセージ', i18n.t('MSG_TALENT_FILE_UPLOADING_WARNING'), 'loading');
            return;
        }
        setFileLoading((prev) => ({ ...prev, [item]: true }));

        try {
            const stableFile = await getStableFile(file);
            const res = await dispatch(uploadFileRegistration({ 
                formData: {
                    file: stableFile,
                    type: item,
                    token: token || "",
                }
            }));

            if(res && res.payload) {
                if(res.payload.success) {
                    setFormData((prev) => ({
                        ...prev,
                        [item]: {
                            ...prev[item],
                            file_name: stableFile.name,
                            file_path: URL.createObjectURL(stableFile),
                            created_at: null,
                            file: stableFile,
                            file_id: res.payload.data
                        },
                    }));
                } else {
                    const errMsg = typeof res.payload === 'string'
                        ? res.payload
                        : (res.payload?.message || i18n.t('MSG_GLOBAL_INTERNAL_SERVER_ERROR'));
                    showToast('メッセージ', errMsg, 'error');
                }
            }
        } catch (error) {
            showToast('メッセージ', i18n.t('MSG_TALENT_UPLOAD_FILE_EMPTY_OR_UNREADABLE'), 'error');
        } finally {
            setFileLoading((prev) => ({ ...prev, [item]: false }));
        }
    }
    
    const handleClearFile = (item:any) => {
        if (Object.values(fileLoading).some((value) => value)) {
            showToast('メッセージ', i18n.t('MSG_TALENT_FILE_UPLOADING_WARNING'), 'loading');
            return;
        }
        setFormData((prev) => ({
            ...prev,
            [item]: null,
        }));
    }

    const handleDownloadFileAttachment = async (url: string, filename?: string) => {
        if (Object.values(fileLoading).some((value) => value)) {
            showToast('メッセージ', i18n.t('MSG_TALENT_FILE_UPLOADING_WARNING'), 'loading');
            return;
        }
        try {
            await downloadFileAttachment(url, filename);
        } catch (error) {
            showToast('メッセージ', i18n.t('MSG_GLOBAL_FILE_NOT_EXIST_OR_DELETED'), 'error');
        }
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (Object.values(fileLoading).some((value) => value)) {
            showToast('メッセージ', i18n.t('MSG_TALENT_FILE_UPLOADING_WARNING'), 'loading');
            return;
        }

        onSubmit(event);
    };

    return (
        <div className="flex flex-col items-center justify-center mt-4 px-6 pb-6 md:pb-0 md:px-0">
            <form onSubmit={handleSubmit} className="bg-white w-full">
                <BaseFormSection formData={formData} setFormData={setFormData} errors={errors} onChange={onChange} onBlur={onBlur}
                    options={options} setters={setters} getValueByObject={getValueByObject} />
                {type !== "internal_talent" && <div className="mb-4 md:w-[346px]">
                    <label className="block mb-2 text-sm text-[#343A3D] pointer-events-none" htmlFor="referer">
                        紹介者
                    </label>
                    <TextInput 
                        disabled={isLoadingRegister} 
                        id="referer"
                        name="referer" 
                        value={formData.referer} 
                        onChange={onChange} 
                        onBlur={onBlur} 
                        customClassName="!h-[2.8125rem]"
                        maxLength={201}
                        tabindex={13}
                    />
                </div>}

                <div className={`${type === "internal_talent" ? "mb-4" : "mb-8"}`}>
                    <div className="flex items-center gap-2 mb-2">
                        <label className="block text-sm leading-none alu-text-base-2 pointer-events-none" htmlFor="city">居住地</label>
                        <CustomTag type="required" />
                    </div>
                    <Dropdown
                        id="city"
                        tabindex={14}
                        error={errors.city}
                        errorText={errors.city}
                        disabled={isLoadingRegister}
                        placeholder={"選択してください"}
                        options={options.cityOptions} value={formData.city}
                        onChange={(value: any) => {
                            setFormData((prev: any) => ({...prev, city: value,}))
                        }}
                    />
                </div>
                {type !== "internal_talent" && <div className="mb-4">
                    <p className="text-base font-bold alu-text-base-2">経歴について教えてください</p>
                </div>}
                {type === "internal_talent" && <div className="mb-4 md:w-[346px]">
                    <label className="block mb-2 text-sm text-[#343A3D] pointer-events-none" htmlFor="referer">
                        社員番号
                    </label>
                    <TextInput 
                        tabindex={15}
                        disabled={isLoadingRegister} 
                        id="employee_number"
                        name="employee_number" 
                        value={formData.employee_number} 
                        onChange={(e: any) => {
                            const value = e.target.value;
                            const regex = /^[A-Za-z0-9-_]*$/;

                            if (regex.test(value) && value.length <= 16) {
                            onChange(e);
                            }
                        }}
                        onBlur={onBlur} 
                        customClassName="!h-[2.8125rem]" 
                        maxLength={16}
                    />
                </div>}
                {type !== "internal_talent" && (
                <>
                <div className="mb-4 flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                        <label className="block text-sm leading-none alu-text-base-2 pointer-events-none">経験した職種</label>
                        <CustomTag type="required" />
                    </div>
                    <label className="block text-sm text-[#343A3D] pointer-events-none" htmlFor="profession_1">職種１</label>
                    <div className="md:w-[346px]">
                    <Dropdown 
                        error={errors.profession_1}
                        errorText={errors.profession_1}
                        inputWidth="full" 
                        disabled={isLoadingRegister} 
                        placeholder={"選択してください"} 
                        options={jobType1Options} 
                        value={formData.profession_1}
                        onChange={(value: any) => handleChangeJobTypeLevel1(value)}
                        tabindex={16}
                    />
                    </div>
                    <label className="block text-sm text-[#343A3D] pointer-events-none" htmlFor="profession_2">職種２</label>
                    <div className="md:w-[346px]">
                    <Dropdown 
                        error={errors.profession_2}
                        errorText={errors.profession_2}
                        inputWidth="full" 
                        disabled={isLoadingRegister || !formData.profession_1} 
                        placeholder={"選択してください"} 
                        options={jobType2Options} 
                        value={formData.profession_2}
                        onChange={(value: any) => handleChangeJobTypeLevel2(value)}
                        tabindex={17}
                    />
                    </div>
                    <label className="block text-sm text-[#343A3D] pointer-events-none" htmlFor="profession_3">職種３</label>
                    <div className="md:w-[346px]">
                    <Dropdown 
                        error={errors.profession_3}
                        errorText={errors.profession_3}
                        inputWidth="full" 
                        disabled={isLoadingRegister || !formData.profession_2} 
                        placeholder={"選択してください"} 
                        options={jobType3Options} 
                        value={formData.profession_3}
                        onChange={(value: any) => {
                            setFormData((prev: any) => ({...prev, profession_3: value,}))
                        }}
                        tabindex={18}
                    />
                    </div>
                </div>

                <div className="mb-4 flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                        <label className="block text-sm leading-none alu-text-base-2 pointer-events-none">経験した業種</label>
                        <CustomTag type="required" />
                    </div>
                    <label className="block text-sm text-[#343A3D] pointer-events-none" htmlFor="industry1">業種１</label>
                    <div className="md:w-[346px]">
                    <Dropdown 
                        error={errors.industry1}
                        errorText={errors.industry1}
                        inputWidth="full" 
                        disabled={isLoadingRegister} 
                        placeholder={"選択してください"} 
                        options={industry1Options} 
                        value={formData.industry1}
                        onChange={(value: any) => handleChangeIndustryLevel1(value)}
                        tabindex={19}
                    />
                    </div>
                    <label className="block text-sm text-[#343A3D] pointer-events-none" htmlFor="industry2">業種２</label>
                    <div className="md:w-[346px]">
                    <Dropdown 
                        error={errors.industry2}
                        errorText={errors.industry2}
                        inputWidth="full" 
                        disabled={isLoadingRegister || !formData.industry1} 
                        placeholder={"選択してください"} 
                        options={industry2Options} 
                        value={formData.industry2}
                        onChange={(value: any) => handleChangeIndustryLevel2(value)}
                        tabindex={20}
                    />
                    </div>
                    <label className="block text-sm text-[#343A3D] pointer-events-none" htmlFor="industry3">業種３</label>
                    <div className="md:w-[346px]">
                    <Dropdown 
                        error={errors.industry3}
                        errorText={errors.industry3}
                        inputWidth="full" 
                        disabled={isLoadingRegister || !formData.industry2} 
                        placeholder={"選択してください"} 
                        options={industry3Options} 
                        value={formData.industry3}
                        onChange={(value: any) => {
                            setFormData((prev: any) => ({...prev, industry3: value}))
                        }}
                        tabindex={21}
                    />
                    </div>
                </div>
                </>)}

                { type == 'alumni' && (
                <>
                <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                        <label className="block text-sm leading-none alu-text-base-2 pointer-events-none" htmlFor="department">最終部署・社名</label>
                        <CustomTag type="required" />
                    </div>
                    <TextInput 
                        disabled={isLoadingRegister} 
                        error={errors.department} 
                        errorText={errors.department} 
                        id="department" name="department" 
                        value={formData.department} 
                        onChange={onChange} 
                        onBlur={onBlur} 
                        placeholder="入力してください" 
                        customClassName="!h-[2.8125rem]" 
                        maxLength={255}
                        tabindex={22}
                    />
                </div>
                
                <div className="mb-4 flex flex-col gap-2 md:gap-[0.625rem]">
                <div className="w-full md:w-[346px]">
                    <label className="block mb-2 text-sm alu-text-base-2" htmlFor="yearOfJoin">
                        入社年月
                    </label>
                    <div className="flex items-top gap-4">
                        <div className="min-w-[8.375rem]">
                            <Dropdown
                                disabled={isLoadingRegister}
                                inputWidth="full"
                                error={errors.yearOfJoin}
                                errorText={errors.yearOfJoin}
                                placeholder={"西暦"}
                                options={dataYear}
                                value={formData.yearOfJoin}
                                unitLabel="年"
                                onChange={(value: any) => setFormData((prev) => ({ ...prev, yearOfJoin: value }))}
                                tabindex={23}
                            />
                        </div>
                        <div className="w-1/3">
                            <Dropdown
                                disabled={isLoadingRegister}
                                inputWidth="full"
                                error={errors.monthOfJoin}
                                errorText={errors.monthOfJoin}
                                placeholder={"月"}
                                options={dataMonth}
                                value={formData.monthOfJoin}
                                unitLabel="月"
                                onChange={(value: any) => setFormData((prev) => ({ ...prev, monthOfJoin: value })) }
                                tabindex={24}
                            />
                        </div>
                        <div className="w-1/3 mt-2">〜</div>
                    </div>
                </div>
                <div className="w-fit hidden">
                    <label className="block mb-2 text-sm alu-text-base-2" htmlFor="dateOfBirth">&nbsp;</label>
                    <div className="w-full h-[2.25rem] inline-flex items-center">〜</div>
                </div>
                
                <div className="w-full mt-2 md:mt-0">
                    <label className="block mb-2 text-sm alu-text-base-2" htmlFor="yearOfLeave">
                    退社年月日
                    </label>
                    <div className="flex items-top gap-4">
                        <div className="flex gap-2">
                            <div className="min-w-[8.375rem]">
                                <Dropdown
                                    disabled={isLoadingRegister}
                                    inputWidth="full"
                                    error={errors.yearOfLeave}
                                    errorText={errors.yearOfLeave}
                                    placeholder={"西暦"}
                                    options={dataYear}
                                    value={formData.yearOfLeave}
                                    unitLabel="年"
                                    onChange={(value: any) => changeYearOfLeave(value) }
                                    tabindex={25}
                                />
                            </div>
                            <div className="shrink-0">
                                <CustomTag type="required" />
                            </div>
                        </div>
                        <div className="w-1/3 md:w-[88px]">
                            <Dropdown
                                disabled={isLoadingRegister}
                                inputWidth="full"
                                error={errors.monthOfLeave}
                                errorText={errors.monthOfLeave}
                                placeholder={"ー"}
                                options={dataMonth}
                                value={formData.monthOfLeave}
                                unitLabel="月"
                                onChange={(value: any) => changeMonthOfLeave(value)}
                                tabindex={26}
                            />
                        </div>
                        <div className="w-1/3 md:w-[88px]">
                            <Dropdown
                                disabled={isLoadingRegister}
                                inputWidth="full"
                                error={errors.dateOfLeave}
                                errorText={errors.dateOfLeave}
                                placeholder={"ー"}
                                options={options.dayOfLeaveOptions}
                                value={formData.dateOfLeave}
                                unitLabel="日"
                                onChange={(value: any) => setFormData((prev) => ({ ...prev, dateOfLeave: value }))}
                                tabindex={27}
                            />
                        </div>
                    </div>
                </div>
                </div>

                <div className={`${options.showOtherReason ? "mb-2" : "mb-8"} inline-block`}>
                <label className="block mb-2 text-sm alu-text-base-2" htmlFor="reason">退職理由</label>
                {reasonList.map((item, idx) => (
                    <div className="flex gap-2 items-center" key={idx}>
                    <CheckboxButton
                        tabindex={28}
                        disabled={isLoadingRegister}
                        id={"reason" + idx}
                        name="reason[]"
                        label={item.name}
                        value={item.checked}
                        onChange={()=>handleCheckboxChange(item, idx)}
                    />
                    </div>
                    ))}
                {errors.reason && (<p className="alu-text-error text-sm mt-2">{errors.reason ? `*${errors.reason}` : ""}</p>)}
                </div>

                {options.showOtherReason && 
                <div className='mb-8'>
                    <CustomTextarea disabled={isLoadingRegister} error={errors.otherReason} errorText={errors.otherReason} rows={3} id="otherReason"
                        name="otherReason" value={formData.otherReason} placeholder="入力してください(任意)"
                        onChange={changeOtherReason} size={"lg"} tabindex={29}/>
                </div>
                }
                </>
                )}
                {type !== "internal_talent" &&
                    <div className={`mb-8 ${type === TALENT_TYPE.EXTERNAL ? "mt-8" : ""}`}>
                        <h2 className="text-base font-bold alu-text-base-2 mb-1">経歴書類のアップロード</h2>
                        <p className="text-sm alu-text-base-1 mb-6">対応形式：PDF、Excel、Word（最大 32 MB）</p>
                        <div className="flex flex-col gap-6">
                            {["RESUME", "CV", "OTHER"].map((item) => {
                            let file = formData[item];
            
                            return (
                                <div key={item}>
                                    <p className="text-sm alu-text-base-2 mb-2">{item === "RESUME" ? "履歴書" : item === "CV" ? "職務経歴書" : "その他のファイル"}</p>
                                    {fileLoading[item] ? (
                                        <div className="w-full h-[10rem] border border-dashed border-[#E0E0E0] flex items-center justify-center">
                                            <LoadingIcon />
                                        </div>
                                    ) : file ? (
                                        <FileItem 
                                            file={file} 
                                            onFileClick={() => handleDownloadFileAttachment(file.file_path, file.file_name)}
                                            handleClearFile={() => handleClearFile(item)} 
                                        />
                                    ) : (
                                        <FileUploader onFileChange={(file) => handleChangeFile(file, item)} />
                                    )}
                                </div>
                            )
                            })}
                        </div>
                    </div>
                }
                <PolicySection isAgree={isAgree} handleChangeAgree={handleChangeAgree} />
                <div className="mb-4 md:mb-0 mt-0 md:mt-[2rem] flex justify-center">
                    <CustomButton type="submit" disabled={!isAgree || isLoadingRegister || options.isSendConfirmMail}
                    customStyle="min-w-full md:!min-w-[8.75rem]"
                    >
                    <p>{isLoadingRegister ? "保存中..." : "同意して登録"}</p>
                    {isLoadingRegister && (
                        <span><LoadingSpinner/></span>
                    )}
                    </CustomButton>
                </div>
            </form>
        </div>
    );
};

export default RegistrationForm;
